import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import * as client from "../../../client";
import { useClientId } from "../../../ClientConfig";
import { useToast } from "../../../context-providers/Toast";
import { useReport, useUpdateReport } from "../data";
import { policyApplicationKeys } from "../queryKeys";
import { DocumentsTab } from "./DocumentsTab";
import { FormSelectionTab } from "./FormSelectionTab";
import { PolicyPane } from "./PolicyPane";
import { SubmissionPane } from "./SubmissionPane";
import {
  usePolicyApplicationFields,
  usePolicyApplications,
  useUpdatePolicyApplication,
  useUpdatePolicyOverlay,
} from "./data";
import { useSubmissionDraft } from "./submissionDraft";
import { statusLabel, statusSeverity } from "./util";
import panes from "./OverviewPanes.module.css";
import styles from "./PolicyApplicationWorkspace.module.css";

// Lazy-loaded so superdoc (the docx editor) is split into its own chunk,
// fetched only when the quote tab is opened.
const QuoteTab = React.lazy(() =>
  import("./QuoteTab").then((m) => ({ default: m.QuoteTab }))
);

type FieldValue = client.ResolvedField["value"];

type WorkspaceTab = "overview" | "forms" | "quote" | "documents";

const TABS: { key: WorkspaceTab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "forms", label: "Form Selection" },
  { key: "quote", label: "Quote" },
  { key: "documents", label: "Documents" },
];

const has = (obj: Record<string, FieldValue>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

const isBlank = (v: FieldValue): boolean =>
  v === null ||
  v === "" ||
  v === undefined ||
  (typeof v === "number" && Number.isNaN(v));

const fieldValue = (fields: client.ResolvedField[], key: string): FieldValue =>
  fields.find((f) => f.key === key)?.value ?? null;

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

export const PolicyApplicationWorkspace: React.FC = () => {
  const { applicationId = "" } = useParams<{ applicationId: string }>();
  const clientId = useClientId();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = usePolicyApplicationFields(applicationId);
  const reportId = data?.application.report_id ?? "";
  const { data: report } = useReport(reportId || undefined);
  const { data: siblings } = usePolicyApplications(reportId);
  const updateOverlay = useUpdatePolicyOverlay(applicationId);
  const updateDetails = useUpdatePolicyApplication(applicationId);
  const updateReport = useUpdateReport(reportId);

  const layersById = useMemo(
    () =>
      new Map<string, client.Layer>(
        (report?.report_json.layer_info ?? []).flatMap((layer) =>
          layer.layer_id ? [[layer.layer_id, layer] as const] : []
        )
      ),
    [report]
  );
  const layer = data ? layersById.get(data.application.layer_id) : undefined;
  const submission = useSubmissionDraft(report, layer, clientId);

  // --- Overlay draft (policy-level overrides) ---
  const saved = data?.application.working_set.overlay;
  const savedKey = saved ? JSON.stringify(saved) : "";
  const [draft, setDraft] = useState<client.PolicyFieldOverlay | null>(null);
  const [tab, setTab] = useState<WorkspaceTab>("overview");

  // Reset the draft whenever the server overlay changes (initial load + save).
  // Keyed on savedKey (a JSON snapshot) so a local edit never triggers a reset.
  useEffect(() => {
    if (saved) {
      setDraft({
        upserts: { ...saved.upserts },
        removals: [...saved.removals],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the snapshot, see above
  }, [savedKey]);

  const overlayDirty = draft !== null && savedKey !== JSON.stringify(draft);
  const overlayEditCount =
    draft && saved
      ? [
          ...new Set([
            ...Object.keys(draft.upserts),
            ...Object.keys(saved.upserts),
          ]),
        ].filter(
          (key) =>
            !has(draft.upserts, key) ||
            !has(saved.upserts, key) ||
            draft.upserts[key] !== saved.upserts[key]
        ).length
      : 0;

  const setUpsert = (key: string, value: FieldValue) =>
    setDraft((d) =>
      d
        ? {
            upserts: { ...d.upserts, [key]: value },
            removals: d.removals.filter((k) => k !== key),
          }
        : d
    );

  const revert = (key: string) =>
    setDraft((d) => {
      if (!d) return d;
      const upserts = { ...d.upserts };
      delete upserts[key];
      return { upserts, removals: d.removals.filter((k) => k !== key) };
    });

  // --- Status draft ---
  const savedStatus = data?.application.status;
  const [status, setStatus] = useState<client.PolicyApplicationStatus | null>(
    null
  );
  useEffect(() => {
    setStatus(savedStatus ?? null);
  }, [savedStatus]);
  const statusDirty = status !== null && status !== savedStatus;

  const dirty = overlayDirty || statusDirty || submission.dirty;
  const policyEditCount = overlayEditCount + (statusDirty ? 1 : 0);

  const discard = () => {
    if (saved) {
      setDraft({
        upserts: { ...saved.upserts },
        removals: [...saved.removals],
      });
    }
    setStatus(savedStatus ?? null);
    submission.reset();
  };

  const saving =
    updateOverlay.isPending ||
    updateDetails.isPending ||
    updateReport.isPending;

  const save = async () => {
    if (!data || !draft || !status) return;
    try {
      // Report first: the overlay response resolves fields against the saved
      // report, so it must see the new submission values.
      const reportUpdate = submission.buildUpdate();
      if (reportUpdate) await updateReport.mutateAsync(reportUpdate);
      if (statusDirty) await updateDetails.mutateAsync({ status });
      if (overlayDirty) {
        await updateOverlay.mutateAsync({
          overlay: draft,
          notes: data.application.working_set.notes,
        });
      } else if (reportUpdate) {
        queryClient.invalidateQueries({
          queryKey: policyApplicationKeys.fields(applicationId),
        });
      }
      toast.show({
        severity: "success",
        summary: "Saved",
        detail: reportUpdate
          ? "Submission and policy changes saved."
          : "Policy changes saved.",
      });
    } catch (e) {
      toast.show({
        severity: "error",
        summary: "Save failed",
        detail: e instanceof Error ? e.message : "Something went wrong.",
      });
    }
  };

  if (isLoading || !data || !draft || !report || !submission.draft || !status) {
    return (
      <div className={styles.loading}>
        <ProgressSpinner style={{ width: 44, height: 44 }} />
      </div>
    );
  }

  const { application, fields } = data;
  const insured = fieldValue(fields, "insured:named");
  const effective = fieldValue(fields, "dates:effective");
  const expiration = fieldValue(fields, "dates:expiration");
  const title = insured ? String(insured) : "Policy Application";

  const missingRequired = fields.some(
    (f) =>
      f.required &&
      isBlank(
        has(draft.upserts, f.key) ? (draft.upserts[f.key] ?? null) : f.value
      )
  );

  const saveHint = missingRequired ? (
    <>
      <i className="pi pi-exclamation-circle" /> Required fields blank
    </>
  ) : (
    <>
      <i className="pi pi-pencil" />
      {[
        submission.editCount > 0 &&
          `${plural(submission.editCount, "submission edit")}`,
        policyEditCount > 0 && `${plural(policyEditCount, "policy edit")}`,
      ]
        .filter(Boolean)
        .join(" · ") || "Unsaved changes"}
    </>
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <span className={styles.kicker}>Policy Application</span>
            <h1 className={styles.title}>
              {title}
              <span className={styles.suffix}>{application.suffix}</span>
            </h1>
            {effective && (
              <div className={styles.dates}>
                {String(effective)}
                {expiration ? ` – ${String(expiration)}` : ""}
              </div>
            )}
          </div>
          <Tag
            value={statusLabel(application.status)}
            severity={statusSeverity(application.status)}
            rounded
          />
        </div>

        <nav className={styles.tabs}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <div className={styles.content}>
        {tab === "overview" && (
          <div className={panes.split}>
            <SubmissionPane
              report={report}
              layer={layer}
              policyCount={siblings?.length ?? 1}
              fields={fields}
              submission={{ ...submission, draft: submission.draft }}
            />
            <PolicyPane
              application={application}
              reportPolicyNumber={report.policy_number}
              siblings={siblings ?? [application]}
              layersById={layersById}
              fields={fields}
              overlay={{ draft, setUpsert, revert }}
              details={{ status, setStatus }}
            />
          </div>
        )}
        {tab === "forms" && (
          <FormSelectionTab
            applicationId={applicationId}
            reportId={application.report_id}
          />
        )}
        {tab === "quote" && (
          <React.Suspense fallback={<ProgressSpinner />}>
            <QuoteTab
              applicationId={applicationId}
              reportId={application.report_id}
            />
          </React.Suspense>
        )}
        {tab === "documents" && (
          <DocumentsTab
            applicationId={applicationId}
            reportId={application.report_id}
          />
        )}
      </div>

      {dirty && (
        <div className={styles.saveBar}>
          <span className={styles.saveHint}>{saveHint}</span>
          <Button label="Discard" text onClick={discard} disabled={saving} />
          <Button
            label="Save changes"
            icon="pi pi-check"
            loading={saving}
            disabled={missingRequired}
            onClick={save}
          />
        </div>
      )}
    </div>
  );
};
